/**
 * GiftMira — cart drawer behaviour.
 *
 * 🔴 DESIGN RULE, LEARNED THE HARD WAY:
 * Horizon morphs the cart drawer on every cart change and reconciles children against the
 * SERVER's HTML. Anything rendered on the client is erased. `data-skip-node-update` does not
 * help — morph.js:248 skips the node's own attributes and then still calls updateChildren().
 *
 * So: the cross-sell and the cutoff line are now rendered in LIQUID. This file does only the
 * two things that genuinely need JavaScript:
 *   1. keep the cutoff countdown TICKING between server renders (never the first paint)
 *   2. handle Add clicks, via a DOCUMENT-level listener — document survives morph, the drawer
 *      markup does not
 *
 * ⛔ Never patch window.fetch to detect cart changes. The real signal is the standard event
 *    `shopify:cart:lines-update` (Shopify @shopify/events), dispatched on document.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';

const CUTOFF_HOUR = 17; // must match snippets/gm-cart-cutoff.liquid + gm-shipping-estimate.liquid
const TZ_STD = -8;
const OBSERVE_DST = true;
const HOLIDAYS = [
  '2026-01-01','2026-01-19','2026-02-16','2026-05-25','2026-06-19','2026-07-03',
  '2026-09-07','2026-10-12','2026-11-11','2026-11-26','2026-12-25',
  '2027-01-01','2027-01-18','2027-02-15','2027-05-31','2027-06-18','2027-07-05',
  '2027-09-06','2027-10-11','2027-11-11','2027-11-25','2027-12-24',
];

const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

function minutesToCutoff() {
  const now = new Date();
  const month = now.getUTCMonth();
  const offset = OBSERVE_DST && month > 2 && month < 10 ? TZ_STD + 1 : TZ_STD;
  const local = new Date(now.getTime() + offset * 3600000);
  const today = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()));

  const dow = today.getUTCDay();
  if (dow === 0 || dow === 6 || HOLIDAYS.includes(dayKey(today))) return null;
  if (local.getUTCHours() >= CUTOFF_HOUR) return null;

  const mins = (CUTOFF_HOUR - local.getUTCHours()) * 60 - local.getUTCMinutes();
  return mins > 0 ? mins : null;
}

/**
 * Keeps the server-rendered countdown current between renders.
 * It never clears the text: if the cutoff has passed, Liquid will simply omit the whole element
 * on the next render. Blanking it here is what made the timer flicker before.
 */
class GmCartCutoff extends HTMLElement {
  connectedCallback() {
    this._timer = setInterval(() => this.tick(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this._timer); // morph removes/re-adds nodes; never leak a timer
  }

  tick() {
    const target = this.querySelector('[data-gm-cutoff-text]');
    const mins = minutesToCutoff();
    if (!target || mins === null) return;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    // ⛔ Never "ships today" — processing is 1–3 business days. Beating the cutoff
    // starts the order today, it does not dispatch it today. Keep this string
    // identical to snippets/gm-cart-cutoff.liquid and the PDP countdown.
    target.innerHTML = `Order within <strong>${h > 0 ? `${h}h ` : ''}${m}m</strong> to start it today`;
  }
}

if (!customElements.get('gm-cart-cutoff')) customElements.define('gm-cart-cutoff', GmCartCutoff);

/**
 * Cross-sell "Add" — delegated on document so it survives every morph.
 */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const btn = target?.closest('[data-gm-xsell-add]');
  if (!btn || btn.disabled) return;

  event.preventDefault();
  const id = Number(btn.getAttribute('data-gm-xsell-add'));
  if (!id) return;

  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding...';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('add failed'))))
    .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()))
    .then((cart) => {
      // 🔴 FIX 7 Oct 2026: Horizon 4.2's CartItemsComponent and cart-icon only react to this
      // event through `event.promise` (component-cart-items.js #handleCartUpdate: `event.promise?.then`).
      // The old version dispatched a bare CustomEvent with no promise, so the item WAS added
      // server-side but the drawer never re-rendered and the button sat on "Adding..." forever.
      // Resolving with no `sections` makes the drawer fall back to its own #refreshCartSection.
      const payload = { cart: { totalQuantity: cart.item_count }, detail: { itemCount: cart.item_count } };
      const evt = new CustomEvent(CART_LINES_UPDATE, { bubbles: true, detail: { action: 'add' } });
      evt.action = 'add';
      evt.promise = Promise.resolve(payload);
      document.dispatchEvent(evt);
      // Safety net: if the morph has not replaced this button within 4s, never leave it stuck.
      setTimeout(() => {
        if (btn.isConnected && btn.disabled) {
          btn.textContent = 'Added ✓';
        }
      }, 4000);
    })
    .catch(() => {
      btn.disabled = false;
      btn.textContent = label;
    });
});
